import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/authContext';
import { useOrgFeatureState } from '../lib/orgFeatures';
import { api, throwIfNotOk } from '../lib/api';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { TextInput } from '@siliconx/shared/ui/Field';

export const Route = createFileRoute('/team')({
  component: TeamPage,
});

interface TeamMember {
  id: string;
  email: string;
  role: 'admin' | 'member';
  isActive: boolean;
  teamId: string | null;
  invitedAt: string | null;
  lastLoginAt: string | null;
}

interface Team {
  id: string;
  name: string;
  createdAt: string | null;
  memberCount: number;
  assetCount: number;
}

function roleBadgeCls(role: 'admin' | 'member') {
  return cn(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
    role === 'admin' ? 'bg-brand-100 text-brand-700' : 'bg-muted text-muted-foreground',
  );
}

function Toast({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const res = await api.api.client.me.team.invite.$post({ json: { email, role: 'member' } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      setEmail('');
      onInvited();
      onClose();
    } catch (ex: unknown) {
      setErr((ex as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-4 text-base font-semibold text-foreground">Invite Team Member</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
            <TextInput
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Role</label>
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              Member (admins cannot be invited via this form)
            </div>
          </div>
          {err && <p className="text-xs text-red-600">{err}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted">
              Cancel
            </button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function statusBadgeCls(active: boolean) {
  return cn(
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
    active ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground',
  );
}

// --------------------------------------------------------------------------
// Member management drawer — opened by clicking a member row. Holds all the
// per-member actions (activate/deactivate, team, resend invite, reset password).
// --------------------------------------------------------------------------
function ManageMemberDrawer({
  member,
  teams,
  isSelf,
  onClose,
  onAssignTeam,
  onDeactivate,
  onReactivate,
  onResendInvite,
  onResetPassword,
}: {
  member: TeamMember;
  teams: Team[];
  isSelf: boolean;
  onClose: () => void;
  onAssignTeam: (userId: string, teamId: string | null) => Promise<void>;
  onDeactivate: (userId: string) => Promise<void>;
  onReactivate: (userId: string) => Promise<void>;
  onResendInvite: (userId: string) => Promise<void>;
  onResetPassword: (userId: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function run(action: string, fn: () => Promise<void>) {
    setBusy(action);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-foreground break-all">{member.email}</h2>
            <div className="mt-2 flex items-center gap-2">
              <span className={roleBadgeCls(member.role)}>{member.role}</span>
              <span className={statusBadgeCls(member.isActive)}>{member.isActive ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted">
            Close
          </button>
        </div>

        {member.role === 'member' && (
          <div className="mt-6">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Team</label>
            <select
              value={member.teamId ?? ''}
              disabled={busy !== null}
              onChange={(e) => void run('team', () => onAssignTeam(member.id, e.target.value || null))}
              className="w-full rounded-md border border-border px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
            >
              <option value="">— none —</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mt-6 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">Actions</p>

          {member.isActive ? (
            <button
              type="button"
              disabled={isSelf || busy !== null}
              onClick={() => void run('deactivate', () => onDeactivate(member.id))}
              title={isSelf ? 'You cannot deactivate yourself' : undefined}
              className={cn(
                'w-full rounded-md border px-3 py-2 text-sm font-medium',
                isSelf ? 'cursor-not-allowed border-border text-muted-foreground' : 'border-red-200 text-red-600 hover:bg-red-50',
              )}
            >
              {busy === 'deactivate' ? 'Deactivating…' : 'Deactivate member'}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void run('reactivate', () => onReactivate(member.id))}
              className="w-full rounded-md border border-green-200 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
            >
              {busy === 'reactivate' ? 'Reactivating…' : 'Reactivate member'}
            </button>
          )}

          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void run('resend', () => onResendInvite(member.id))}
            className="w-full rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {busy === 'resend' ? 'Sending…' : 'Resend invite'}
          </button>

          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void run('reset', () => onResetPassword(member.id))}
            className="w-full rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
          >
            {busy === 'reset' ? 'Sending…' : 'Reset password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Teams management — create / rename / delete.
// --------------------------------------------------------------------------
function TeamsSection({
  teams,
  reload,
  notify,
}: {
  teams: Team[];
  reload: () => void;
  notify: (m: string, t: 'success' | 'error') => void;
}) {
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const res = await api.api.client.teams.$post({ json: { name: newName.trim() } });
      await throwIfNotOk(res);
      setNewName('');
      reload();
      notify('Team created', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function rename(id: string) {
    if (!editName.trim()) return;
    try {
      const res = await api.api.client.teams[':teamId'].$patch({
        param: { teamId: id },
        json: { name: editName.trim() },
      });
      await throwIfNotOk(res);
      setEditingId(null);
      reload();
      notify('Team renamed', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  async function remove(t: Team) {
    if (!confirm(`Delete team "${t.name}"?`)) return;
    try {
      const res = await api.api.client.teams[':teamId'].$delete({ param: { teamId: t.id } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      reload();
      notify('Team deleted', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-foreground">Teams</h2>
      <p className="text-xs text-muted-foreground">
        Members are scoped to their team&apos;s assets; admins see everything. Assign a member&apos;s team below, and
        assets to teams from the Assets page.
      </p>

      <form onSubmit={create} className="flex gap-2">
        <TextInput className="w-64"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New team name"
        />
        <Button className="px-3" type="submit" disabled={busy || !newName.trim()}>
          Add Team
        </Button>
      </form>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {teams.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">No teams yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-2">Team</th>
                <th className="px-4 py-2">Members</th>
                <th className="px-4 py-2">Assets</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {teams.map((t) => (
                <tr key={t.id} className="hover:bg-muted">
                  <td className="px-4 py-2 font-medium text-foreground">
                    {editingId === t.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void rename(t.id); if (e.key === 'Escape') setEditingId(null); }}
                        className="w-48 rounded-md border border-border px-2 py-1 text-sm"
                      />
                    ) : (
                      t.name
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{t.memberCount}</td>
                  <td className="px-4 py-2 text-muted-foreground">{t.assetCount}</td>
                  <td className="px-4 py-2 text-right">
                    {editingId === t.id ? (
                      <>
                        <button type="button" onClick={() => rename(t.id)} className="rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50">Save</button>
                        <button type="button" onClick={() => setEditingId(null)} className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => { setEditingId(t.id); setEditName(t.name); }} className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted">Rename</button>
                        <button type="button" onClick={() => remove(t)} className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function TeamPage() {
  const { user, isLoading: authLoading } = useAuth();
  const teamsFeature = useOrgFeatureState('teams');
  const canTeams = teamsFeature === 'enabled';
  const navigate = useNavigate();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  // SIL-994/SIL-1034: Team is an orgFeature: 'teams'-gated experience
  // (customerNav.ts hides the nav entry entirely for an org type that
  // doesn't unlock it, and the API rejects team operations against one,
  // clientAccess.ts's access({ orgFeature: 'teams' })) — this is the same
  // reachable-by-URL backstop the root-level `OrgFeatureGate` (__root.tsx)
  // already provides. SIL-1055: target must match OrgFeatureGate's own
  // destination (/dashboard) — Team has no `fallbackTo` (unlike Settings/
  // orgProfile), so there is no substitute page to remap to; this used to
  // send an individual org to /individual-profile instead, which raced
  // OrgFeatureGate's /dashboard redirect and (as the later-mounted effect,
  // Team's page sits inside AppShell's Outlet, after OrgFeatureGate in
  // __root.tsx's render order) always won. Waits for 'pending' to resolve
  // (does not fire while identity is still loading), same as OrgFeatureGate.
  useEffect(() => {
    if (authLoading) return;
    if (teamsFeature === 'pending') return;
    if (teamsFeature === 'disabled') {
      void navigate({ to: '/dashboard', replace: true });
      return;
    }
    if (user?.role !== 'admin') {
      void navigate({ to: '/profile', replace: true });
    }
  }, [authLoading, user, teamsFeature, navigate]);

  const fetchTeam = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.api.client.me.team.$get().then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as TeamMember[];
      }),
      api.api.client.teams.$get().then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as Team[];
      }),
    ])
      .then(([m, t]) => { setMembers(m); setTeams(t); })
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const reloadTeams = useCallback(() => {
    api.api.client.teams
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as Team[];
      })
      .then(setTeams)
      .catch(() => {});
  }, []);

  useEffect(() => {
    // SIL-994/SIL-1034: skip the fetch entirely for an org that doesn't
    // unlock 'teams' — the redirect effect above sends it to /dashboard
    // (SIL-1055), and /api/client/teams + /api/client/me/team now 403 for
    // one anyway (access({ orgFeature: 'teams' })).
    if (!authLoading && user?.role === 'admin' && canTeams) {
      (async () => {
        const res = await api.api.client.me.$get();
        await throwIfNotOk(res);
        const d = (await res.json()) as unknown as { id: string };
        setCurrentUserId(d.id);
      })().catch(() => {});
      fetchTeam();
    }
  }, [authLoading, user, canTeams, fetchTeam]);

  // Shared helper for the 204-returning member action endpoints.
  async function postAction(userId: string, path: 'reactivate' | 'resend-invite' | 'reset-password') {
    const teamUser = api.api.client.me.team[':userId'];
    const res =
      path === 'reactivate'
        ? await teamUser.reactivate.$post({ param: { userId } })
        : path === 'resend-invite'
          ? await teamUser['resend-invite'].$post({ param: { userId } })
          : await teamUser['reset-password'].$post({ param: { userId } });
    if (!res.ok) {
      await throwIfNotOk(res);
      throw new Error(`HTTP ${res.status}`);
    }
  }

  async function handleDeactivate(userId: string) {
    if (!confirm('Deactivate this team member?')) return;
    try {
      const res = await api.api.client.me.team[':userId'].$delete({ param: { userId } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      // Keep the row (now inactive) so the admin can reactivate it.
      setMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, isActive: false } : m)));
      notify('Member deactivated.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  async function handleReactivate(userId: string) {
    try {
      await postAction(userId, 'reactivate');
      setMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, isActive: true } : m)));
      notify('Member reactivated.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  async function handleResendInvite(userId: string) {
    try {
      await postAction(userId, 'resend-invite');
      notify('Invite re-sent.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  async function handleResetPassword(userId: string) {
    try {
      await postAction(userId, 'reset-password');
      notify('Password reset email sent.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  async function assignMemberTeam(userId: string, teamId: string | null) {
    try {
      const res = await api.api.client.teams.user[':userId'].$put({ param: { userId }, json: { teamId } });
      await throwIfNotOk(res);
      setMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, teamId } : m)));
      reloadTeams(); // member counts change
      notify('Team assignment updated', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    }
  }

  const selectedMember = members.find((m) => m.id === selectedMemberId) ?? null;

  if (authLoading) return <LoadingShell rows={4} />;
  if (user?.role !== 'admin') return null;

  return (
    <>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <PageHeader title="Team" pinTo="/team" />
          <Button size="sm" className="text-sm" onClick={() => setInviteOpen(true)}>
            Invite Member
          </Button>
        </div>

        <TeamsSection teams={teams} reload={reloadTeams} notify={notify} />

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Members</h2>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {loading ? (
              <div className="p-6 text-sm text-muted-foreground">Loading members…</div>
            ) : error ? (
              <div className="p-6 text-sm text-red-600">Couldn&apos;t load team members. {error}</div>
            ) : members.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No team members found.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Team</th>
                    <th className="px-4 py-3">Invited</th>
                    <th className="px-4 py-3">Last active</th>
                    <th className="px-4 py-3 text-right">Manage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {members.map((m) => {
                    const teamName = m.role === 'admin'
                      ? 'All teams'
                      : (teams.find((t) => t.id === m.teamId)?.name ?? '—');
                    return (
                      <tr
                        key={m.id}
                        onClick={() => setSelectedMemberId(m.id)}
                        className="cursor-pointer hover:bg-muted"
                      >
                        <td className="px-4 py-3 font-medium text-foreground">{m.email}</td>
                        <td className="px-4 py-3"><span className={roleBadgeCls(m.role)}>{m.role}</span></td>
                        <td className="px-4 py-3"><span className={statusBadgeCls(m.isActive)}>{m.isActive ? 'Active' : 'Inactive'}</span></td>
                        <td className="px-4 py-3 text-muted-foreground">{teamName}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.invitedAt ? new Date(m.invitedAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.lastLoginAt ? new Date(m.lastLoginAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium text-brand-600">Manage →</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {selectedMember && (
        <ManageMemberDrawer
          member={selectedMember}
          teams={teams}
          isSelf={currentUserId !== null && selectedMember.id === currentUserId}
          onClose={() => setSelectedMemberId(null)}
          onAssignTeam={assignMemberTeam}
          onDeactivate={handleDeactivate}
          onReactivate={handleReactivate}
          onResendInvite={handleResendInvite}
          onResetPassword={handleResetPassword}
        />
      )}

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => { fetchTeam(); notify('Invitation sent!', 'success'); }}
      />

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </>
  );
}
